<template>
  <InvestorOnboardingStep>
    <form @submit.prevent="next">
      <FormGroup>
        <Text variant="h2">How should Tallo reach you?</Text>

        <RadioButtonGroup direction="column">
          <RadioCard name="channel" :value="PreferredCommunicationChannel.Sms" :disabled="isSaving" v-model="channel">
            <View direction="row" gap="sm" align="center">
              <IconTile icon="phone" size="small" color="orange" :coloredIcon="true" />
              <View direction="column" gap="xs">
                <View direction="row" gap="sm" align="center">
                  <Text variant="label-medium">Text messages</Text>
                  <Badge label="Recommended" variant="violet" size="small" />
                </View>
                <Text variant="body-medium" color="secondary">
                  Texts go both ways. Reply “book it” to a tour request, or text Tallo anytime.
                </Text>
              </View>
            </View>
          </RadioCard>
          <RadioCard name="channel" :value="PreferredCommunicationChannel.Email" :disabled="isSaving" v-model="channel">
            <View direction="row" gap="sm" align="center">
              <IconTile icon="mail-01" size="small" color="violet" :coloredIcon="true" />
              <View direction="column" gap="xs">
                <Text variant="label-medium">Email</Text>
                <Text variant="body-medium" color="secondary">
                  Updates land in your inbox. Tour requests wait until you check it.
                </Text>
              </View>
            </View>
          </RadioCard>
        </RadioButtonGroup>

        <div class="phone-wrap">
          <Transition name="fade-down">
            <FormField
              label="Phone number"
              :error="form.fieldErrors('phoneNumber')"
              v-show="channel === PreferredCommunicationChannel.Sms"
            >
              <InputPhone v-model="form.fields.phoneNumber" @blur="form.validation.phoneNumber.$touch()" />
              <Text v-if="needsVerification" variant="body-small" color="secondary" class="phone-hint">
                We’ll text a code to confirm this number.
              </Text>
              <Text v-else-if="channel === PreferredCommunicationChannel.Sms" variant="body-small" color="secondary">
                Verified
              </Text>
            </FormField>
          </Transition>
        </div>

        <View direction="row" justify="end" gap="sm">
          <Button variant="ghost" type="button" :disabled="isSaving" @click="goBack">Back</Button>
          <Button type="submit" :disabled="!canContinue" :loading="isSaving">Continue</Button>
        </View>
      </FormGroup>
    </form>
  </InvestorOnboardingStep>

  <VerifyPhoneModal
    ref="verifyPhoneModalRef"
    :phone-number="user?.phoneNumber || ''"
    @verified="onPhoneVerified"
    @closed="isVerifyingPhone = false"
  />
</template>

<script setup lang="ts">
import { requiredIf } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import {
  Badge,
  Button,
  FormField,
  FormGroup,
  IconTile,
  InputPhone,
  RadioButtonGroup,
  RadioCard,
  Text,
  View,
  phoneNumberValidator,
  readableServerMessage,
  serverErrorCode,
  useForm,
} from '@tallo/design-system';
import {
  InvestorOnboardingStep as OnboardingStep,
  PreferredCommunicationChannel,
  firstMissingOnboardingStep,
  investorOnboardingService,
  phoneTakenErrorCode,
  useInvestorStore,
  useOnboardingStep,
  useOnboardingStore,
} from '@tallo/investor/investor';
import { AppRouteNames } from '@tallo/investor/navigation';
import { normalizePhoneNumber } from '@tallo/utility';

import VerifyPhoneModal from '../../profile/modals/verify-phone-modal.vue';
import InvestorOnboardingStep from '../components/investor-onboarding-step.vue';

const store = useOnboardingStore();
const investorStore = useInvestorStore();
const router = useRouter();

const verifyPhoneModalRef = ref<InstanceType<typeof VerifyPhoneModal>>();
// While the code modal is open the wizard's Enter shortcut must stay off, or Enter in the code
// field would run Continue again and reopen the modal on its first screen
const isVerifyingPhone = ref(false);

// The phone and its verified stamp live on the user, which the verification modal keeps fresh
const user = computed(() => investorStore.investor?.user);
// Local until saved: the store only ever holds what the server has. A fresh account opens on texts,
// the recommended channel, so the phone field is already there to fill in
const channel = ref(store.preferredCommunicationChannel ?? PreferredCommunicationChannel.Sms);

const form = useForm<{ phoneNumber: string }>(
  { phoneNumber: user.value?.phoneNumber || '' },
  {
    phoneNumber: computed(() => ({
      requiredIf: requiredIf(() => channel.value === PreferredCommunicationChannel.Sms),
      phoneNumberValidator,
    })),
  },
);

const isPhoneDirty = computed(
  () => normalizePhoneNumber(form.fields.phoneNumber) !== normalizePhoneNumber(user.value?.phoneNumber),
);
// The server's verdict on a number is about that number: it goes when the number changes
watch(
  () => form.fields.phoneNumber,
  () => form.clearValidationErrors(),
);

// The same rule as the profile page: SMS is earned by verifying the number it goes to
const needsVerification = computed(
  () => channel.value === PreferredCommunicationChannel.Sms && (!user.value?.phoneVerifiedAt || isPhoneDirty.value),
);

const isFormValid = computed(() => {
  if (!channel.value) return false;
  if (channel.value === PreferredCommunicationChannel.Sms) {
    return !!form.fields.phoneNumber && !form.validation.phoneNumber.$invalid;
  }
  return true;
});

const { goBack, returnTo, isSaving, canContinue, save, onContinue } = useOnboardingStep(
  () => isFormValid.value && !isVerifyingPhone.value,
);

async function next() {
  form.validation.$touch();
  if (!canContinue.value) return;

  if (channel.value === PreferredCommunicationChannel.Email) {
    await finish(PreferredCommunicationChannel.Email);
    return;
  }

  if (!needsVerification.value) {
    await finish(PreferredCommunicationChannel.Sms);
    return;
  }

  // The code is texted to the stored number, so the number is saved first
  await save(async () => {
    if (isPhoneDirty.value) {
      const phoneNumber = form.fields.phoneNumber;
      try {
        await investorOnboardingService.saveSignUpInfo({ phoneNumber });
      } catch (error) {
        // A number another account holds is the field's problem: it stays under the input, and
        // Continue waits for a different number
        if (serverErrorCode(error) !== phoneTakenErrorCode) throw error;
        form.setFieldError('phoneNumber', readableServerMessage(error) ?? 'That number belongs to another account.');
        return;
      }
      // The server drops the verified stamp with the new number; mirror it so the hint and the modal agree
      if (user.value) investorStore.setUser({ ...user.value, phoneNumber, phoneVerifiedAt: null });
    }
    isVerifyingPhone.value = true;
    verifyPhoneModalRef.value?.show();
  }, "We couldn't save your phone number. Please try again.");
}

async function onPhoneVerified() {
  // The modal reports success before it closes, so lift the shortcut lock ourselves
  isVerifyingPhone.value = false;
  await finish(PreferredCommunicationChannel.Sms);
}

async function finish(preferredCommunicationChannel: PreferredCommunicationChannel) {
  await save(async () => {
    await store.saveStep(OnboardingStep.Communication, { preferredCommunicationChannel });

    try {
      await store.complete();
    } catch (error) {
      const missingStep = firstMissingOnboardingStep(error);
      if (!missingStep) throw error;
      // The server keeps the record of finished steps, and this tab's copy can fall behind it
      // (another tab changed the account type, say): take its record and go back to the step it names
      await store.load();
      returnTo(missingStep);
      return;
    }

    // The server has the flag; mirror it so Finish opens even if the refresh below fails
    investorStore.markInitialOnboardingCompleted();
    router.push({ name: AppRouteNames.INVESTOR_ONBOARDING_FINISH });
    investorStore.load().catch((error) => console.error('Failed to refresh the investor after onboarding', error));
  }, "We couldn't save your preferences. Please try again.");
}

onContinue(next);
</script>

<style scoped>
.phone-wrap {
  min-height: 86px;
  display: grid;
  align-items: start;
}

.phone-hint {
  margin-top: var(--t-spacing-xs);
}

.fade-down-enter-active,
.fade-down-leave-active {
  transition:
    opacity 180ms var(--t-ease-out),
    transform 180ms var(--t-ease-out);
}

.fade-down-enter-from,
.fade-down-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .fade-down-enter-from,
  .fade-down-leave-to {
    transform: none;
  }
}
</style>
